import React, {useMemo, useEffect, useState} from "react";
import BackgroundVideo from "@/components/BackgroundVideo";
import GridLayer from "@/layers/GridLayer";
import TopPanelLayer from "@/layers/TopPanelLayer";
import ScreenSaverLayer from "@/layers/ScreenSaverLayer";
import IntroTextLayer from "@/layers/IntroTextLayer";
import {AnimatePresence} from "motion/react";
import {useNav} from "@/hooks/useNav";
import IntroUniverseLayer from "@/layers/IntroUniverseLayer";
import {ScreensaverVideo} from "@/components/videos";
import BriefingVideo from "@/components/videos/BriefingVideo";
import VideoSlider from "@/components/VideoSlider";
import TaskPanel from "@/components/TaskPanel";
import CodeEditor from "@/components/CodeEditor";
import AwardLayer from "../layers/AwardLayer";
import EndLayer from "@/layers/EndLayer";
import EndVideo from "@/components/videos/EndVideo";
import ShareVideo from "@/components/videos/ShareVideo";
import SharePanel from "@/components/SharePanel";
import BottomPanelLayer from "@/layers/BottomPanelLayer";
import StatesToggle from "@/components/StatesToggle";
import MusicController from "@/components/MusicController";
import {r} from "@/utils/responsive";
import VideoEditorOverlay from "@/components/videos/VideoEditorOverlay";
import MobileTabletScreen from "@/components/MobileTabletScreen";
import ExitModalConfirm from "@/components/ExitModalConfirm";
import {S3_FILES} from "@/config/s3_files";
import ReactPlayerWrapper from "@/components/ReactPlayerWrapper";
import {motion} from "motion/react";
import {initializeAppState} from "@/stores/progressStore";

interface LayoutProps {
    children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({children}) => {
    const {currentSection, isTransitioning} = useNav();
    const [isMobileOrTablet, setIsMobileOrTablet] = useState(() => {
        if (typeof window === 'undefined') return false;
        try {
            const media = window.matchMedia('(pointer: coarse)');
            return media.matches || window.innerWidth < 1440;
        } catch {
            return window.innerWidth < 1440;
        }
    });
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [showExitModal, setShowExitModal] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const showStartVideo = useMemo(
        () =>
            currentSection === "intro_text" ||
            currentSection === "screensaver" ||
            currentSection === "intro_universe",
        [currentSection],
    );
    const showBriefVideo = useMemo(
        () => currentSection === "briefing" || currentSection === "editor",
        [currentSection],
    );

    // Инициализация приложения с восстановлением прогресса
    useEffect(() => {
        const initialize = async () => {
            try {
                await initializeAppState();
            } catch (error) {
                console.warn('Failed to initialize app state:', error);
            } finally {
                setIsInitialized(true);
            }
        };
        
        initialize();
    }, []);

    // Проверка размера экрана и типа устройства
    useEffect(() => {
        const media = window.matchMedia('(pointer: coarse)');
        const checkScreenSize = () => {
            const ua = navigator.userAgent || '';
            const width = window.innerWidth;
            const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0;
            const isCoarse = media.matches;
            const uaMobile = /Mobi|Android(?!.*Tablet)|iPhone|iPod/i.test(ua);
            const uaTablet = /iPad|Tablet|SM-T|Tab/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua));

            const mobile = (width < 768) || (isCoarse && width < 900) || uaMobile;
            const tablet = !mobile && ((width >= 768 && width < 1440) || isCoarse || uaTablet || (isTouch && width < 1440));

            setIsMobile(mobile);
            setIsTablet(tablet);
            setIsMobileOrTablet(mobile || tablet);
        };

        // Проверка при загрузке
        checkScreenSize();

        // Слушаем изменения размера окна и изменение типа указателя
        window.addEventListener("resize", checkScreenSize);
        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', checkScreenSize);
        } else if (typeof media.addListener === 'function') {
            // @ts-ignore
            media.addListener(checkScreenSize);
        }

        return () => {
            window.removeEventListener("resize", checkScreenSize);
            if (typeof media.removeEventListener === 'function') {
                media.removeEventListener('change', checkScreenSize);
            } else if (typeof media.removeListener === 'function') {
                // @ts-ignore
                media.removeListener(checkScreenSize);
            }
        };
    }, []);

    // Если экран меньше 1440px, показываем только MobileTabletScreen
    if (isMobileOrTablet) {
        return (
            <main
                style={{
                    position: "fixed",
                    inset: 0,
                    overflow: "hidden",
                    background: "#110B29",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <MobileTabletScreen/>
            </main>
        );
    }

    // Показываем лоадинг пока не инициализирован стор
    if (!isInitialized) {
        return (
            <main
                style={{
                    position: "fixed",
                    inset: 0,
                    overflow: "hidden",
                    background: "#110B29",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                }}
            >
                <div>Загрузка...</div>
            </main>
        );
    }

    return (
        <main
            style={{
                position: "fixed",
                inset: 0,
                overflow: "hidden",
            }}
        >
            <VideoSlider show={showStartVideo} direction="up">
                <GridLayer/>
                {/*{(currentSection === 'intro_text' || currentSection === 'intro_universe') && (*/}
                {/*    <VideoEditorOverlay/>*/}
                {/*)}*/}
            </VideoSlider>

            <VideoSlider show={showBriefVideo} direction="down">
                <AnimatePresence>
                    {currentSection === "editor" && <VideoEditorOverlay/>}
                </AnimatePresence>
                {/*<BriefingVideo/>*/}
                <ReactPlayerWrapper
                    blurOnSections={["editor"]}
                    videoSrc={S3_FILES.BRIEF_VIDEO}
                    posterSrc={S3_FILES.BRIEF_VIDEO_POSTER}
                    blur={200}
                    style={{
                        zIndex: -1,
                    }}
                />
                <TaskPanel/>
            </VideoSlider>
            <TopPanelLayer/>
            <BottomPanelLayer/>
            <AnimatePresence>
                {currentSection === "editor" && <CodeEditor/>}
            </AnimatePresence>
            <AnimatePresence>
                {currentSection === "screensaver" && <ScreenSaverLayer/>}
            </AnimatePresence>
            <AnimatePresence>
                {currentSection === "intro_text" && <IntroTextLayer/>}
            </AnimatePresence>
            <AnimatePresence>
                {currentSection === "intro_universe" && <IntroUniverseLayer/>}
            </AnimatePresence>
            <motion.div
                initial={{
                    opacity: currentSection === "end" ? 1 : 0,
                }}
                animate={{
                    opacity: currentSection === "end" ? 1 : 0,
                }}
            >
                <ReactPlayerWrapper
                    videoSrc={S3_FILES.END_VIDEO}
                    posterSrc={S3_FILES.END_VIDEO_POSTER}
                />
            </motion.div>
            <AnimatePresence>
                {currentSection === "end" && (
                    <>
                        <EndLayer/>
                    </>
                )}
            </AnimatePresence>
            <motion.div
                initial={{
                    opacity: currentSection === "share" ? 1 : 0,
                }}
                animate={{
                    opacity: currentSection === "share" ? 1 : 0,
                }}
            >
                <ReactPlayerWrapper
                    videoSrc={S3_FILES.SHARE_VIDEO}
                    posterSrc={S3_FILES.SHARE_VIDEO_POSTER}
                    style={{
                        right: 0,
                        top: "50%",
                        transform: "translateY(-50%) scale(1.25)",
                        transformOrigin: "right",
                    }}
                />
            </motion.div>
            <AnimatePresence>
                {currentSection === "share" && (
                    <>
                        <SharePanel/>
                    </>
                )}
            </AnimatePresence>
            <AwardLayer/>
            {/*<StatesToggle/>*/}
            <MusicController/>
            <ExitModalConfirm/>
            {children}
        </main>
    );
};

export default Layout;
